import React, { useState, useMemo } from 'react';
import './OptimizationResults.css';

const OptimizationResults = ({ results, selectedFunds = [] }) => {
  const [investmentAmount, setInvestmentAmount] = useState(100000);
  const [inputValue, setInputValue] = useState('1,00,000');

  const formatPercentage = (value) => `${(value * 100).toFixed(2)}%`;
  const formatNumber = (value) => value.toFixed(4);

  // Format number with Indian numbering system (lakhs, crores)
  const formatIndianCurrency = (num) => {
    const formatted = num.toLocaleString('en-IN');
    return `₹${formatted}`;
  };

  // Parse Indian formatted number back to number
  const parseIndianNumber = (str) => {
    const cleaned = str.replace(/[₹,\s]/g, '');
    const num = parseInt(cleaned, 10);
    return isNaN(num) ? 0 : num;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    const parsed = parseIndianNumber(value);
    if (parsed > 0) {
      setInvestmentAmount(parsed);
    }
  };

  const handleAmountBlur = () => {
    // Format the input on blur
    if (investmentAmount > 0) {
      setInputValue(investmentAmount.toLocaleString('en-IN'));
    }
  };

  // Create a lookup map for asset types from selectedFunds
  const assetTypeMap = useMemo(() => {
    const map = {};
    selectedFunds.forEach(fund => {
      map[String(fund.schemeCode)] = fund.assetType || 'mutual_fund';
    });
    return map;
  }, [selectedFunds]);

  // Sort weights by allocation (descending) and add asset type
  const sortedWeights = useMemo(() => {
    return Object.entries(results.weights)
      .sort((a, b) => b[1].weight - a[1].weight)
      .filter(([_, data]) => data.weight > 0.001)
      .map(([code, data]) => {
        // Determine asset type: prefer backend type, then selectedFunds lookup, then pattern-based detection
        let assetType = data.type;
        if (!assetType) {
          assetType = assetTypeMap[String(code)];
        }
        if (!assetType) {
          // Numeric codes are mutual funds, alphanumeric are stocks
          assetType = /^\d+$/.test(code) ? 'mutual_fund' : 'stock';
        }
        return {
          code,
          ...data,
          assetType
        };
      });
  }, [results.weights, assetTypeMap]);

  // Separate MFs and Stocks
  const mutualFunds = sortedWeights.filter(w => w.assetType === 'mutual_fund');
  const stocks = sortedWeights.filter(w => w.assetType === 'stock' || w.assetType === 'etf');

  // Calculate totals for each category
  const mfTotalWeight = mutualFunds.reduce((sum, w) => sum + w.weight, 0);
  const stockTotalWeight = stocks.reduce((sum, w) => sum + w.weight, 0);

  return (
    <div className="optimization-results">
      <div className="results-summary">
        <div className="summary-card">
          <h3>Portfolio Performance</h3>
          <div className="metric-grid">
            <div className="metric">
              <span className="metric-label">Expected Annual Return</span>
              <span className="metric-value positive">{formatPercentage(results.expected_return)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Annual Volatility (Risk)</span>
              <span className="metric-value neutral">{formatPercentage(results.volatility)}</span>
            </div>
            <div className="metric">
              <span className="metric-label">Sharpe Ratio</span>
              <span className="metric-value">{formatNumber(results.sharpe_ratio)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="allocation-table">
        <div className="allocation-header">
          <h3>Recommended Allocation</h3>
          <div className="investment-input-container">
            <label htmlFor="investmentAmount">Investment Amount:</label>
            <div className="investment-input-wrapper">
              <span className="currency-symbol">₹</span>
              <input
                id="investmentAmount"
                type="text"
                value={inputValue}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                className="investment-input"
                placeholder="Enter amount"
              />
            </div>
          </div>
        </div>

        {/* Asset Allocation Summary */}
        {(mutualFunds.length > 0 && stocks.length > 0) && (
          <div className="asset-allocation-summary">
            <div className="allocation-pie">
              <div className="pie-segment mf-segment" style={{ flex: mfTotalWeight }}>
                <span>MF: {formatPercentage(mfTotalWeight)}</span>
              </div>
              <div className="pie-segment stock-segment" style={{ flex: stockTotalWeight }}>
                <span>Stocks: {formatPercentage(stockTotalWeight)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Mutual Funds Section */}
        {mutualFunds.length > 0 && (
          <div className="asset-section mf-section">
            <h4 className="section-header mf-header-results">
              🇮🇳 Mutual Funds ({mutualFunds.length}) - {formatPercentage(mfTotalWeight)} = {formatIndianCurrency(Math.round(mfTotalWeight * investmentAmount))}
            </h4>
            <table>
              <thead>
                <tr>
                  <th>Scheme Code</th>
                  <th>Fund Name</th>
                  <th>Allocation</th>
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {mutualFunds.map((item) => (
                  <tr key={item.code}>
                    <td className="fund-code">{item.code}</td>
                    <td className="fund-name">{item.name}</td>
                    <td className="allocation">
                      <div className="allocation-bar-container">
                        <div
                          className="allocation-bar mf-bar"
                          style={{ width: `${item.weight * 100}%` }}
                        />
                        <span className="allocation-text">{formatPercentage(item.weight)}</span>
                      </div>
                    </td>
                    <td className="amount">
                      {formatIndianCurrency(Math.round(item.weight * investmentAmount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Stocks/ETFs Section */}
        {stocks.length > 0 && (
          <div className="asset-section stock-section">
            <h4 className="section-header stock-header-results">
              🌍 Stocks & ETFs ({stocks.length}) - {formatPercentage(stockTotalWeight)} = {formatIndianCurrency(Math.round(stockTotalWeight * investmentAmount))}
            </h4>
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Name</th>
                  <th>Allocation</th>
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {stocks.map((item) => (
                  <tr key={item.code}>
                    <td className="fund-code">{item.code}</td>
                    <td className="fund-name">{item.name}</td>
                    <td className="allocation">
                      <div className="allocation-bar-container">
                        <div
                          className="allocation-bar stock-bar"
                          style={{ width: `${item.weight * 100}%` }}
                        />
                        <span className="allocation-text">{formatPercentage(item.weight)}</span>
                      </div>
                    </td>
                    <td className="amount">
                      {formatIndianCurrency(Math.round(item.weight * investmentAmount))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="optimization-info">
        <h4>Optimization Details</h4>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Objective:</span>
            <span className="info-value">{results.optimization_params.objective}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Max Allocation:</span>
            <span className="info-value">{formatPercentage(results.optimization_params.constraints.max_allocation)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Data Period:</span>
            <span className="info-value">
              {new Date(results.data_info.start_date).toLocaleDateString()} - {new Date(results.data_info.end_date).toLocaleDateString()}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">Data Points:</span>
            <span className="info-value">{results.data_info.num_days} days</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizationResults;